import { Observable, of } from 'rxjs';
import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AuthService } from './auth.service';
import { ApiService } from './api.service';
import { HelperService } from './helper.service';
import moment from 'moment';
import 'moment-timezone';
import { DateHelperService } from './date-helper.service';
import { tap, share, finalize, map } from 'rxjs/operators';
import {
  ProfileSettings,
  SimpleUser,
  User,
  UserQRAuthenticator,
  UserSearchResult,
  UserToken,
  LabelWithId,
} from '../interfaces';
import { environment } from '../../../environments/environment';
import { EmitterService } from './emitter.service';
import { ProfileSettingsUpdatedEvent } from '../models/events';
import { EntityParticipant, EntityParticipantsService } from './entity-participants.service';
import { PLATFORM_CHAT_ENTITY_TYPE } from '../components/platform-comments-chat/platform-chat-entity.const';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private cache: Record<string, User> = {};
  private cachedObservable: Record<string, Observable<User>> = {};

  users: SimpleUser[] = [];

  private readonly entityParticipants: EntityParticipantsService = inject(EntityParticipantsService);

  constructor(
    private readonly authService: AuthService,
    private readonly apiService: ApiService,
    private readonly helperService: HelperService,
    private readonly dateHelperService: DateHelperService,
    private readonly httpClient: HttpClient
  ) {}

  public getToken(): Observable<UserToken> {
    return this.apiService.httpGet('/api/users/token/renew');
  }

  public getCachedUserByUsername(username: string): SimpleUser | undefined {
    return this.users.find((usr: SimpleUser) => usr.username === username);
  }

  public findUserByUsername(
    username: string,
    fromCache: boolean,
    cacheRequest: boolean
  ): Observable<User | SimpleUser | undefined> {
    if (fromCache) {
      const user: SimpleUser | undefined = this.getCachedUserByUsername(username);
      return of(user);
    } else {
      let observable: Observable<User>;
      if (cacheRequest) {
        if (this.cache[username]) {
          observable = of(this.cache[username]);
        } else if (this.cachedObservable[username]) {
          observable = this.cachedObservable[username];
        } else {
          this.cachedObservable[username] = this.apiService.httpGet('/api/users/' + username).pipe(
            tap((res: User) => (this.cache[username] = res)),
            share(),
            finalize(() => {
              this.cachedObservable[username] = void 0;
              this.cache[username] = void 0;
            })
          );
          observable = this.cachedObservable[username];
        }
      } else {
        observable = this.apiService.httpGet('/api/users/' + username);
      }
      return observable;
    }
  }

  public getSingleUserInfo(username: string): Observable<User> {
    return this.apiService.httpGet('/api/users/' + username);
  }

  public getUsersByCompanyId(companyId: string, isOwners?: boolean): Observable<User[]> {
    let path: string = `/api/users/company/${companyId}`;
    if (isOwners) {
      path += `?isOwners=${isOwners}`;
    }
    return this.apiService.httpGet(path);
  }

  /**
   * Workspace users via the unified per-entity participants endpoint (same as the task
   * surfaces); the legacy /api/workspace/{companyId}/{workspaceId}/users listing is retired.
   * companyId is kept for signature compatibility. Rows are a partial User shape
   * (username/name/email/avatar — no role or activation data).
   */
  public getUserByWorkspaceId(_companyId: string, workspaceId: string): Observable<User[]> {
    return this.entityParticipants
      .getParticipants(PLATFORM_CHAT_ENTITY_TYPE.WORKSPACE, workspaceId)
      .pipe(map((participants: EntityParticipant[]) => participants as unknown as User[]));
  }

  public updateProfileSettings(data: ProfileSettings): Observable<ProfileSettings> {
    return this.apiService
      .httpPost('/api/users/' + this.authService.getAuthUserName() + '/profile-settings', data)
      .pipe(
        tap((res: ProfileSettings) => {
          EmitterService.get(ProfileSettingsUpdatedEvent).emit(res);
        })
      );
  }

  public getUserProfileSettings(): Observable<ProfileSettings> {
    return this.apiService.httpGet('/api/users/' + this.authService.getAuthUserName() + '/profile-settings');
  }

  public getAccountSettings(): Observable<User> {
    return this.getSingleUserInfo(this.authService.getAuthUserName());
  }

  public updateUser(data: any, extend?: boolean): Observable<User> {
    return this.apiService.httpPut(
      '/api/users/' + data.username,
      extend
        ? data
        : {
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            jobTitle: data.jobTitle,
            password: data.password,
          }
    );
  }

  public changePassword(currentPassword: string, newPassword: string, reNewPassword: string): Observable<void> {
    const username = encodeURIComponent(this.authService.getAuthUserName() ?? '');
    return this.httpClient.put<void>(
      `${environment.iam.iamApiUrl}/api/v1/users/${username}/password`,
      { currentPassword, newPassword, reNewPassword },
      { withCredentials: true }
    );
  }

  public getUsersByActivationStatus(
    activationStatus: string,
    pagingState: string = null,
    includeBrandId?: boolean
  ): Observable<UserSearchResult> {
    let url: string = `/api/users/activation-status/${activationStatus}`;
    if (pagingState && pagingState != '') {
      url = url + `?pagingState=${pagingState}`;
    }
    if (includeBrandId) {
      url += pagingState && pagingState != '' ? '&includeBrandId=true' : 'includeBrandId=true';
    }
    return this.apiService.httpGet(url).pipe(
      map((result: any) => {
        const userResult: UserSearchResult = result as UserSearchResult;
        userResult.data.forEach((user: User) => {
          user.avatarUrl = this.helperService.getFixedAvatarUrl(user.avatarUrl, this.authService.getAuthSession());
        });
        return userResult;
      })
    );
  }

  public getUsersByActivationStatusWithQueryParams(
    activationStatus: string,
    pagingState: string,
    fullName: string,
    firstName: string,
    lastName: string,
    email: string,
    includeBrandId?: boolean
  ): Observable<UserSearchResult> {
    let url: string = `/api/users/activation-status/${activationStatus}?fullName=${fullName}&firstName=${firstName}&lastName=${lastName}&email=${email}&`;
    if (pagingState && pagingState != '') {
      url = url + `&pagingState=${pagingState}`;
    }
    if (includeBrandId) {
      url += pagingState && pagingState != '' ? '&includeBrandId=true' : 'includeBrandId=true';
    }
    return this.apiService.httpGet(url).pipe(
      map((result: any) => {
        const userResult: UserSearchResult = result as UserSearchResult;
        userResult.data.forEach((user: User) => {
          if (user.avatarUrl) {
            user.avatarUrl = this.helperService.getFixedAvatarUrl(user.avatarUrl, this.authService.getAuthSession());
          }
        });
        return userResult;
      })
    );
  }

  public getAuthenticatorInfo(username: string): Observable<UserQRAuthenticator> {
    return this.apiService.httpGet(`/api/users/${username}/profile-settings/mfa/authenticator`);
  }

  public updateAuthenticatorInfo(username: string, token: string, factorId: string): Observable<boolean> {
    return this.apiService.httpPost(
      `/api/users/${username}/profile-settings/mfa/authenticator?token=${token}&factorId=${factorId}`,
      {}
    );
  }

  /**
   * Delete user
   * @param user
   */
  public deleteUser(username: string): Observable<any> {
    return this.apiService.httpDelete('/api/users/' + username);
  }

  public deleteMultipleUsers(usernames: string[]): Observable<any> {
    return this.apiService.httpDelete(`/api/users/multiDeleteUser`, usernames);
  }

  public sendInvitationEmail(email: string): Observable<void> {
    return this.apiService.httpPost('/api/registration/resendEmail/' + email, {});
  }

  public disableUser(email: string): Observable<void> {
    return this.apiService.httpPut(`/api/users/${email}/block`, {});
  }

  public enableUser(email: string): Observable<void> {
    return this.apiService.httpPut(`/api/users/${email}/unblock`, {});
  }
  /**
   * Return list of timezones
   * @returns Array of timezone options with id and formatted label
   */
  public getTimezones(): LabelWithId<string>[] {
    const timezones: LabelWithId<string>[] = [];
    const momentTimezones: string[] = moment.tz.names();

    momentTimezones.forEach((tzName: string) => {
      timezones.push({
        id: tzName,
        label: this.dateHelperService.formatTimezoneName(tzName),
      });
    });

    return timezones;
  }

  public getNotificationsSettings(workspacId: string): Observable<any> {
    return this.apiService.httpGet(
      '/api/users/' + this.authService.getAuthUserName() + `/${workspacId}/usernotificationssettings`
    );
  }

  public getUserNotificationsSettings(workspacId: string, projectId: string): Observable<any> {
    return this.apiService.httpGet(
      '/api/users/' + this.authService.getAuthUserName() + `/${workspacId}/${projectId}/usernotificationssettings`
    );
  }

  public getProjectNotificationsSettings(workspaceId): Observable<any> {
    return this.apiService.httpGet(
      '/api/users/' + this.authService.getAuthUserName() + `/${workspaceId}/userprojectsnotificationssettings`
    );
  }

  public getPlatformNotificationsSettings(): Observable<any> {
    return this.apiService.httpGet('/api/users/' + this.authService.getAuthUserName() + `/usernotificationssettings`);
  }

  public updateProjectNotificationsSettings(data: any): Observable<any> {
    return this.apiService.httpPost(
      '/api/users/' +
        this.authService.getAuthUserName() +
        `/${data.workspaceId}/${data.projectId}/usernotificationssettings`,
      data
    );
  }

  public updateNotificationsSettings(data: any): Observable<any> {
    return this.apiService.httpPost(
      '/api/users/' + this.authService.getAuthUserName() + `/${data.workspaceId}/usernotificationssettings`,
      data
    );
  }

  public updateUserNotificationsSettings(workspaceId: string, projectId: string, data: any): Observable<any> {
    return this.apiService.httpPost(
      '/api/users/' + this.authService.getAuthUserName() + `/${workspaceId}/${projectId}/usernotificationssettings`,
      data
    );
  }

  public updatePlatformNotificationsSettings(data: any): Observable<any> {
    return this.apiService.httpPost(
      '/api/users/' + this.authService.getAuthUserName() + `/usernotificationssettings`,
      data
    );
  }

  public changeUserProfileSettingLang(language: string): Observable<ProfileSettings> {
    return this.apiService.httpPost(
      `/api/users/${this.authService.getAuthUserName()}/profile-settings/lang/${language}`,
      {}
    );
  }

  public getUserInfo(email: string): string {
    return this.users.find((user: SimpleUser) => user.username === email)?.fullName?.trim() || email.toLowerCase();
  }

  public getPossibleOwnersForProject(projectId: string, username: string): Observable<User[]> {
    return this.apiService.httpGet(`/api/users/${username}/${projectId}/availableUsersProject`);
  }

  public transferOriginalOwnership(projectId: string, oldOwner: string, newOwner: string): Observable<void> {
    return this.apiService.httpPost(
      `/api/users/${oldOwner}/originalownership?projectId=${encodeURIComponent(projectId)}&newUserName=${encodeURIComponent(newOwner)}`,
      {}
    );
  }

  public transferOwnership(username: string, targets: Record<string, string[]>, removeUser: boolean): Observable<any> {
    return this.apiService.httpPost(`/api/users/${username}/ownership?removeUser=${removeUser}`, targets);
  }

  public getAvatarUrl(username: string): string {
    const companyId: string | undefined | null = this.authService.getAuthUser()?.companyId;
    let template: string = environment.web.avatarUrl.replace('%USERNAME%', username);
    if (companyId) {
      template = template.replace('%COMPANYID%', companyId);
    }
    return template;
  }
}
